// Astrea Ops Platform - subcontractor document upload data-access layer
//
// Two audiences share this file. Staff-side (loadPendingUploads/reviewUpload) calls
// are plain RLS-gated Supabase queries against pending_uploads, exactly like every
// other *.js file in src/lib - is_admin_finance() on the table already restricts
// these to Finance/Senior Management, matching Martin's instruction that this queue
// stays narrower than Files access generally.
//
// Public-side (getUploadInfo/initUpload/uploadFileToSignedUrl/confirmUpload) is used
// by the unauthenticated SubcontractorUploadPage in src/App.jsx (reached via
// ?upload=<token>, before the login gate) - it never touches Supabase tables
// directly (RLS would just return nothing to an anonymous caller, since
// is_internal_staff() requires a session - see the migration file's comment), so
// every public-side call here goes through the api/subcontractor-upload-*.js
// serverless functions instead, the same fetch-based pattern src/lib/auth.js's
// grantAccess() already uses for its own privileged call.
//
// uploadFileToSignedUrl uses the anon Supabase client's own
// storage.uploadToSignedUrl() - safe to call with no session, because the signed
// token IS the one-time credential (see api/subcontractor-upload-init.js).

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';

const BUCKET = 'subcontractor-uploads';

// ---- Public (unauthenticated) side ----------------------------------------------

export async function getUploadInfo(token) {
  const res = await fetch('/api/subcontractor-upload-info?token=' + encodeURIComponent(token));
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'This link is no longer valid.');
  return body;
}

export async function initUpload({ token, file, category, note }) {
  const res = await fetch('/api/subcontractor-upload-init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      category: category || null,
      note: note || null,
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Could not start the upload.');
  return body;
}

export async function uploadFileToSignedUrl({ path, signedUploadToken, file }) {
  const { error } = await supabase.storage.from(BUCKET).uploadToSignedUrl(path, signedUploadToken, file);
  if (error) throw error;
}

// v7.76: last/batchIds mark the final file of a batch, so the person who made the link is
// emailed once for the whole batch (api/_uploadNotify.js).
export async function confirmUpload({ token, pendingUploadId, last = false, batchIds = [] }) {
  const res = await fetch('/api/subcontractor-upload-confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(last ? { token, pendingUploadId, last: true, batchIds } : { token, pendingUploadId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'The file did not finish uploading.');
  return body;
}

// ---- Staff (authenticated) side ---------------------------------------------------

function toPendingUpload(row) {
  return {
    id: row.id,
    personId: row.person_id,
    fileName: row.file_name,
    filePath: row.file_path,
    fileSize: row.file_size,
    mimeType: row.mime_type,
    category: row.category || '',
    note: row.note || '',
    status: row.status,
    fileConfirmed: !!row.file_confirmed,
    uploadedAt: row.uploaded_at,
    confirmedAt: row.confirmed_at,
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    reviewNote: row.review_note || '',
    documentId: row.document_id,
  };
}

// Loads every pending_uploads row this signed-in user can see (RLS already limits
// that to Finance/Senior Management - see pending_uploads_select policy). Callers
// typically filter to status==="pending" for the badge/queue count and show the
// rest as history.
export async function loadPendingUploads() {
  const { data, error } = await selectAllRes(() => supabase
    .from('pending_uploads')
    .select('*')
    .order('uploaded_at', { ascending: false })
    .order('id'));
  if (error) throw error;
  return (data || []).map(toPendingUpload);
}

// v7.76 (16 Oct 2026): how many uploads are waiting, for the count on the Uploads tab and
// the alert in the bell. Only finished uploads count (an unfinished one cannot be approved).
// newestId changes when another file arrives, so a dismissed alert comes back for it.
export async function loadPendingUploadSummary() {
  const { data, error } = await selectAllRes(() => supabase
    .from('pending_uploads')
    .select('id, uploaded_at')
    .eq('status', 'pending')
    .eq('file_confirmed', true)
    .order('uploaded_at', { ascending: false })
    .order('id'));
  if (error) throw error;
  const rows = data || [];
  return { count: rows.length, newestId: rows.length ? rows[0].id : null };
}

// v7.76: opens a file waiting for review, with a link that works for five minutes (the same
// endpoint and rule as approved documents - see api/document-url.js).
export async function openPendingUpload(pendingUploadId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/document-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ pendingUploadId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.url) throw new Error(body.error || 'Could not open this upload.');
  return body.url;
}

// Calls the authenticated review endpoint (not a direct Supabase write) because
// approving mints a signed Storage URL and creates the matching `documents` row
// server-side in one step - see api/subcontractor-review-upload.js.
async function reviewUpload({ pendingUploadId, action, reviewNote, category }) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/subcontractor-review-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ pendingUploadId, action, reviewNote, category }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to review this upload.');
  return body;
}

export const approveUpload = (pendingUploadId, category) => reviewUpload({ pendingUploadId, action: 'approve', category });
export const rejectUpload = (pendingUploadId, reviewNote) => reviewUpload({ pendingUploadId, action: 'reject', reviewNote });

// Permanently deletes a pending_uploads row (any status - pending, approved,
// rejected), its Storage file, and the `documents` row if it was approved - see
// api/delete-uploaded-document.js. Same endpoint src/lib/documents.js's
// deletePersonDocument() calls, just entering from the other side (a
// pendingUploadId instead of a documentId); the endpoint resolves the rest itself.
export async function deletePendingUpload(pendingUploadId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in.');
  const res = await fetch('/api/delete-uploaded-document', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ pendingUploadId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Failed to delete this upload.');
  return body;
}
